import { useState } from 'react'
import { Button, Label } from '@creanizer/ui'
import { Download, Copy } from 'lucide-react'

interface ImageGenerateResultProps {
  imageData: string
  mimeType: string
}

export function ImageGenerateResult({ imageData, mimeType }: ImageGenerateResultProps) {
  const imageUrl = `data:${mimeType};base64,${imageData}`
  const [isConverting, setIsConverting] = useState(false)

  const handleDownload = async () => {
    setIsConverting(true)
    try {
      // Canvas経由でPNGに変換してダウンロード
      const img = new Image()
      img.crossOrigin = 'anonymous'

      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = () => reject(new Error('画像の読み込みに失敗しました'))
        img.src = imageUrl
      })

      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('Canvas contextの取得に失敗しました')

      ctx.drawImage(img, 0, 0)

      // PNG形式でBlobを生成
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => {
          if (b) resolve(b)
          else reject(new Error('PNG変換に失敗しました'))
        }, 'image/png')
      })

      // ダウンロード
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `generated-image-${Date.now()}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download error:', error)
      alert('ダウンロードに失敗しました')
    } finally {
      setIsConverting(false)
    }
  }

  const handleCopyBase64 = () => {
    navigator.clipboard.writeText(imageData)
    alert('Base64データをコピーしました')
  }

  return (
    <div className="space-y-4">
      <Label>生成結果</Label>

      {/* 画像プレビュー */}
      <div className="relative border rounded-lg overflow-hidden bg-gray-100">
        <img
          src={imageUrl}
          alt="Generated image"
          className="w-full h-auto max-h-[500px] object-contain"
        />
      </div>

      {/* アクションボタン */}
      <div className="flex gap-2">
        <Button onClick={handleDownload} disabled={isConverting} className="flex-1">
          <Download className="h-4 w-4 mr-2" />
          {isConverting ? '変換中...' : 'PNG ダウンロード'}
        </Button>
        <Button variant="outline" onClick={handleCopyBase64}>
          <Copy className="h-4 w-4 mr-2" />
          Base64コピー
        </Button>
      </div>
    </div>
  )
}
